import React, { useState } from 'react';
import { useCommunity } from '@/contexts/CommunityContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Loader2, 
  Save, 
  Upload, 
  ChevronDown, 
  ChevronUp 
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { getCommunityAssetPath } from '@/utils/communityUtils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const DEFAULT_LOCALE = 'en_GB';

const CommunityManagement = () => {
  const { currentCommunity } = useCommunity();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  
  // State for collapsible advanced settings
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const communityQuery = useQuery({
    queryKey: ['admin-community', currentCommunity.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('*')
        .eq('id', currentCommunity.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!currentCommunity.id
  });

  const [formData, setFormData] = useState({
    name: currentCommunity.name,
    allowed_frontend_domain: currentCommunity.allowed_frontend_domain || '',
    description: currentCommunity.description || '',
    primary_color: currentCommunity.primary_color,
    secondary_color: currentCommunity.secondary_color,
    accent_color: currentCommunity.accent_color,
    privacy_type: (currentCommunity.privacy_type as 'public' | 'private') || 'public',
    default_locale: currentCommunity.default_locale || DEFAULT_LOCALE,
    available_locales: currentCommunity.available_locales || [DEFAULT_LOCALE],
    community_app_url: currentCommunity?.community_app_url || '',
    default_business_banner_url: currentCommunity?.default_business_banner_url || '',
    favicon_url: currentCommunity?.favicon_url || '',
    logo_url: currentCommunity?.logo_url || '',
    logo_2_url: currentCommunity?.logo_2_url || '',
    facebook_url: currentCommunity?.facebook_url || '',
    instagram_url: currentCommunity?.instagram_url || '',
    twitter_url: currentCommunity?.twitter_url || ''
  });

  const [uploading, setUploading] = useState(false);
  const [faviconUploading, setFaviconUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logo2Uploading, setLogo2Uploading] = useState(false);
  const [selectedLocales, setSelectedLocales] = useState<string[]>(
    currentCommunity.available_locales || [DEFAULT_LOCALE]
  );

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase
        .from('communities')
        .update({
          name: data.name,
          allowed_frontend_domain: data.allowed_frontend_domain,
          description: data.description,
          primary_color: data.primary_color,
          secondary_color: data.secondary_color,
          accent_color: data.accent_color,
          privacy_type: data.privacy_type,
          default_locale: data.default_locale,
          available_locales: data.available_locales,
          community_app_url: data.community_app_url,
          default_business_banner_url: data.default_business_banner_url,
          favicon_url: data.favicon_url,
          logo_url: data.logo_url,
          logo_2_url: data.logo_2_url,
          facebook_url: data.facebook_url,
          instagram_url: data.instagram_url,
          twitter_url: data.twitter_url
        })
        .eq('id', currentCommunity.id);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-community'] });
      toast({
        title: t('admin.community.update.success.title', 'Community updated'),
        description: t('admin.community.update.success.description', 'Community settings have been updated successfully.'),
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error', 'Error'),
        description: t('admin.community.update.error', 'Failed to update community settings.'),
        variant: 'destructive',
      });
      console.error('Error updating community:', error);
    }
  });

  React.useEffect(() => {
    if (communityQuery.data) {
      setFormData({
        name: communityQuery.data.name,
        allowed_frontend_domain: communityQuery.data.allowed_frontend_domain || '',
        description: communityQuery.data.description || '',
        primary_color: communityQuery.data.primary_color,
        secondary_color: communityQuery.data.secondary_color,
        accent_color: communityQuery.data.accent_color,
        privacy_type: (communityQuery.data.privacy_type as 'public' | 'private') || 'public',
        default_locale: communityQuery.data.default_locale || DEFAULT_LOCALE,
        available_locales: communityQuery.data.available_locales || [DEFAULT_LOCALE],
        community_app_url: communityQuery.data.community_app_url || '',
        default_business_banner_url: communityQuery.data.default_business_banner_url || '',
        favicon_url: communityQuery.data.favicon_url || '',
        logo_url: communityQuery.data.logo_url || '',
        logo_2_url: communityQuery.data.logo_2_url || '',
        facebook_url: communityQuery.data.facebook_url || '',
        instagram_url: communityQuery.data.instagram_url || '',
        twitter_url: communityQuery.data.twitter_url || ''
      });
      setSelectedLocales(communityQuery.data.available_locales || [DEFAULT_LOCALE]);
    }
  }, [communityQuery.data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePrivacyChange = (isPublic: boolean) => {
    setFormData(prev => ({ 
      ...prev, 
      privacy_type: isPublic ? 'public' : 'private' 
    }));
  };

  const handleDefaultLocaleChange = (locale: string) => {
    setFormData(prev => ({
      ...prev,
      default_locale: locale,
      available_locales: prev.available_locales.includes(locale) 
        ? prev.available_locales 
        : [...prev.available_locales, locale]
    }));
  };

  const handleLocaleToggle = (locale: string) => {
    let newLocales: string[];

    if (selectedLocales.includes(locale)) {
      if (locale === formData.default_locale) {
        toast({
          title: t('admin.community.locale.error.title', 'Cannot remove default locale'),
          description: t('admin.community.locale.error.description', 'The default locale must be included in available locales.'),
          variant: "destructive"
        });
        return;
      }
      newLocales = selectedLocales.filter(l => l !== locale);
    } else {
      newLocales = [...selectedLocales, locale];
    }

    setSelectedLocales(newLocales);
    setFormData(prev => ({ ...prev, available_locales: newLocales }));
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `default-banner-${Date.now()}.${fileExt}`;
    const filePath = getCommunityAssetPath(currentCommunity.id, fileName);
    
    setUploading(true);
    
    try {
      // Check if business_images bucket exists, if not create it
      const { data: buckets } = await supabase.storage.listBuckets();
      const bucketExists = buckets?.find(b => b.name === 'business_images');
      
      if (!bucketExists) {
        await supabase.storage.createBucket('business_images', { public: true });
      }
      
      // Upload file
      const { data, error } = await supabase.storage
        .from('business_images')
        .upload(filePath, file, {
          upsert: true,
          contentType: file.type
        });
      
      if (error) throw error;
      
      // Get public URL for the uploaded file
      const { data: publicURL } = supabase.storage
        .from('business_images')
        .getPublicUrl(filePath);
        
      setFormData(prev => ({
        ...prev,
        default_business_banner_url: publicURL.publicUrl
      }));
      
    } catch (error) {
      console.error('Error uploading banner:', error);
      toast({
        title: t('common.error', 'Error'),
        description: t('admin.community.banner.error', 'Failed to upload banner image.'),
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `favicon-${Date.now()}.${fileExt}`;
    const filePath = getCommunityAssetPath(currentCommunity.id, fileName);
    
    setFaviconUploading(true);
    
    try {
      // Check if business_images bucket exists, if not create it
      const { data: buckets } = await supabase.storage.listBuckets();
      const bucketExists = buckets?.find(b => b.name === 'business_images');
      
      if (!bucketExists) {
        await supabase.storage.createBucket('business_images', { public: true });
      }
      
      // Upload file
      const { data, error } = await supabase.storage
        .from('business_images')
        .upload(filePath, file, {
          upsert: true,
          contentType: file.type
        });
      
      if (error) throw error;
      
      // Get public URL for the uploaded file
      const { data: publicURL } = supabase.storage
        .from('business_images')
        .getPublicUrl(filePath);
        
      setFormData(prev => ({
        ...prev,
        favicon_url: publicURL.publicUrl
      }));
      
    } catch (error) {
      console.error('Error uploading favicon:', error);
      toast({
        title: t('common.error', 'Error'),
        description: t('admin.community.favicon.error', 'Failed to upload favicon image.'),
        variant: 'destructive',
      });
    } finally {
      setFaviconUploading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `logo-${Date.now()}.${fileExt}`;
    const filePath = getCommunityAssetPath(currentCommunity.id, fileName);
    
    setLogoUploading(true);
    
    try {
      // Check if business_images bucket exists, if not create it
      const { data: buckets } = await supabase.storage.listBuckets();
      const bucketExists = buckets?.find(b => b.name === 'business_images');
      
      if (!bucketExists) {
        await supabase.storage.createBucket('business_images', { public: true });
      }
      
      // Upload file
      const { data, error } = await supabase.storage
        .from('business_images')
        .upload(filePath, file, {
          upsert: true,
          contentType: file.type
        });
      
      if (error) throw error;
      
      // Get public URL for the uploaded file
      const { data: publicURL } = supabase.storage
        .from('business_images')
        .getPublicUrl(filePath);
        
      setFormData(prev => ({
        ...prev,
        logo_url: publicURL.publicUrl
      }));
      
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: t('common.error', 'Error'),
        description: t('admin.community.logo.error', 'Failed to upload logo image.'),
        variant: 'destructive',
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogo2Upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    
    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `logo-2-${Date.now()}.${fileExt}`;
    const filePath = getCommunityAssetPath(currentCommunity.id, fileName);
    
    setLogo2Uploading(true);
    
    try {
      // Check if business_images bucket exists, if not create it
      const { data: buckets } = await supabase.storage.listBuckets();
      const bucketExists = buckets?.find(b => b.name === 'business_images');
      
      if (!bucketExists) {
        await supabase.storage.createBucket('business_images', { public: true });
      }
      
      // Upload file
      const { data, error } = await supabase.storage
        .from('business_images')
        .upload(filePath, file, {
          upsert: true,
          contentType: file.type
        });
      
      if (error) throw error;
      
      // Get public URL for the uploaded file
      const { data: publicURL } = supabase.storage
        .from('business_images')
        .getPublicUrl(filePath);
        
      setFormData(prev => ({
        ...prev,
        logo_2_url: publicURL.publicUrl
      }));
      
    } catch (error) {
      console.error('Error uploading logo 2:', error);
      toast({
        title: t('common.error', 'Error'),
        description: t('admin.community.logo2.error', 'Failed to upload secondary logo image.'),
        variant: 'destructive',
      });
    } finally {
      setLogo2Uploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  if (communityQuery.isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      <div className="relative">
        <h1 className="admin-section-title mb-2">{t('admin.community.title', 'Community Management')}</h1>
        <p className="text-muted-foreground/80 text-lg">
          {t('admin.community.subtitle', 'Configure your community settings, appearance, and localization')}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Community Information Section - Simplified with toggle in top right */}
        <Card className="admin-card admin-card-stats mb-6">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4">
            <div>
              <CardTitle>{t('admin.community.info.title', 'Community Information')}</CardTitle>
              <CardDescription>
                {t('admin.community.info.description', 'Basic information about your community')}
              </CardDescription>
            </div>
            <div className="flex items-center justify-center sm:justify-start space-x-2 p-3 bg-gray-50/50 rounded-lg">
              <Switch 
                id="privacy" 
                checked={formData.privacy_type === 'public'}
                onCheckedChange={handlePrivacyChange}
              />
              <Label htmlFor="privacy" className="font-medium">
                {formData.privacy_type === 'public' 
                  ? t('admin.community.public', 'Public')
                  : t('admin.community.private', 'Private')}
              </Label>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('admin.community.name', 'Community Name')}</Label>
              <Input 
                id="name" 
                name="name" 
                value={formData.name} 
                onChange={handleInputChange} 
                required 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('admin.community.description', 'Description')}</Label>
              <Textarea 
                id="description" 
                name="description" 
                value={formData.description} 
                onChange={handleInputChange} 
                rows={4} 
              />
            </div>
            
            {/* Social Media URLs Section */}
            <div className="space-y-4 border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-700">{t('admin.community.social.title', 'Social Media Links')}</h4>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="facebook_url">{t('admin.community.social.facebook', 'Facebook URL')}</Label>
                  <Input 
                    id="facebook_url" 
                    name="facebook_url" 
                    value={formData.facebook_url} 
                    onChange={handleInputChange} 
                    placeholder={t('admin.community.social.facebook.placeholder', 'e.g., https://facebook.com/your-community')} 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instagram_url">{t('admin.community.social.instagram', 'Instagram URL')}</Label>
                  <Input 
                    id="instagram_url" 
                    name="instagram_url" 
                    value={formData.instagram_url} 
                    onChange={handleInputChange} 
                    placeholder={t('admin.community.social.instagram.placeholder', 'e.g., https://instagram.com/your-community')} 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="twitter_url">{t('admin.community.social.twitter', 'Twitter URL')}</Label>
                  <Input 
                    id="twitter_url" 
                    name="twitter_url" 
                    value={formData.twitter_url} 
                    onChange={handleInputChange} 
                    placeholder={t('admin.community.social.twitter.placeholder', 'e.g., https://twitter.com/your-community')} 
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('admin.community.social.description', 'These links will appear in your community footer when provided.')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Appearance Section - Moved up */}
        <Card className="admin-card admin-card-stats mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">{t('admin.community.appearance.title', 'Appearance')}</CardTitle>
            <CardDescription className="text-muted-foreground/80">
              {t('admin.community.appearance.description', 'Customize your community\'s colors and assets')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label htmlFor="primary_color">{t('admin.community.color.primary', 'Primary Color')}</Label>
                <div className="flex space-x-2">
                  <Input 
                    id="primary_color" 
                    name="primary_color" 
                    value={formData.primary_color} 
                    onChange={handleInputChange}
                    className="flex-1" 
                  />
                  <div 
                    className="w-10 h-10 flex-shrink-0 rounded border shadow-sm" 
                    style={{ backgroundColor: formData.primary_color }} 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondary_color">{t('admin.community.color.secondary', 'Secondary Color')}</Label>
                <div className="flex space-x-2">
                  <Input 
                    id="secondary_color" 
                    name="secondary_color" 
                    value={formData.secondary_color} 
                    onChange={handleInputChange}
                    className="flex-1" 
                  />
                  <div 
                    className="w-10 h-10 flex-shrink-0 rounded border shadow-sm" 
                    style={{ backgroundColor: formData.secondary_color }} 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="accent_color">{t('admin.community.color.accent', 'Accent Color')}</Label>
                <div className="flex space-x-2">
                  <Input 
                    id="accent_color" 
                    name="accent_color" 
                    value={formData.accent_color} 
                    onChange={handleInputChange}
                    className="flex-1" 
                  />
                  <div 
                    className="w-10 h-10 flex-shrink-0 rounded border shadow-sm" 
                    style={{ backgroundColor: formData.accent_color }} 
                  />
                </div>
              </div>
            </div>

            {/* Community Logo Upload Section */}
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="logo_upload">{t('admin.community.logo', 'Community Logo')}</Label>
              <div className="space-y-4">
                {formData.logo_url && (
                  <div className="flex items-center space-x-2">
                    <img 
                      src={formData.logo_url} 
                      alt="Community logo" 
                      className="h-12 max-w-32 object-contain rounded border"
                    />
                    <span className="text-sm text-muted-foreground">Current logo</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input 
                    type="hidden" 
                    id="logo_url"
                    name="logo_url"
                    value={formData.logo_url || ''}
                  />
                  <div className="relative w-full">
                    <Input 
                      type="file" 
                      id="logo_upload" 
                      className="hidden" 
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={logoUploading}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      className="w-full"
                      disabled={logoUploading}
                      onClick={() => document.getElementById('logo_upload')?.click()}
                    >
                      {logoUploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      {t('admin.community.logo.upload', 'Upload Logo')}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('admin.community.logo.help', 'Upload a logo for your community header. Recommended size: 200x60 pixels (landscape). Max file size: 2MB. Supported formats: PNG, JPG, SVG. Remember to click Save Changes to apply this update.')}
                </p>
              </div>
            </div>

            {/* Secondary Logo Upload Section */}
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="logo_2_upload">{t('admin.community.logo2', 'Secondary Logo')}</Label>
              <div className="space-y-4">
                {formData.logo_2_url && (
                  <div className="flex items-center space-x-2">
                    <img 
                      src={formData.logo_2_url} 
                      alt="Secondary community logo" 
                      className="h-12 max-w-32 object-contain rounded border"
                    />
                    <span className="text-sm text-muted-foreground">Current secondary logo</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input 
                    type="hidden" 
                    id="logo_2_url"
                    name="logo_2_url"
                    value={formData.logo_2_url || ''}
                  />
                  <div className="relative w-full">
                    <Input 
                      type="file" 
                      id="logo_2_upload" 
                      className="hidden" 
                      accept="image/*"
                      onChange={handleLogo2Upload}
                      disabled={logo2Uploading}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      className="w-full"
                      disabled={logo2Uploading}
                      onClick={() => document.getElementById('logo_2_upload')?.click()}
                    >
                      {logo2Uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      {t('admin.community.logo2.upload', 'Upload Secondary Logo')}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('admin.community.logo2.help', 'Upload an alternative or secondary logo for your community. This can be used for different contexts or layouts. Recommended size: 200x60 pixels (landscape). Max file size: 2MB. Supported formats: PNG, JPG, SVG. Remember to click Save Changes to apply this update.')}
                </p>
              </div>
            </div>

            {/* Favicon Upload Section */}
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="favicon_upload">{t('admin.community.favicon', 'Community Favicon')}</Label>
              <div className="space-y-4">
                {formData.favicon_url && (
                  <div className="flex items-center space-x-2">
                    <img 
                      src={formData.favicon_url} 
                      alt="Community favicon" 
                      className="w-8 h-8 rounded border"
                    />
                    <span className="text-sm text-muted-foreground">Current favicon</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input 
                    type="hidden" 
                    id="favicon_url"
                    name="favicon_url"
                    value={formData.favicon_url || ''}
                  />
                  <div className="relative w-full">
                    <Input 
                      type="file" 
                      id="favicon_upload" 
                      className="hidden" 
                      accept="image/*"
                      onChange={handleFaviconUpload}
                      disabled={faviconUploading}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      className="w-full"
                      disabled={faviconUploading}
                      onClick={() => document.getElementById('favicon_upload')?.click()}
                    >
                      {faviconUploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      {t('admin.community.favicon.upload', 'Upload Favicon')}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('admin.community.favicon.help', 'Upload a favicon for your community. Recommended size: 32x32 pixels. Supported formats: PNG, JPG, ICO. Remember to click Save Changes to apply this update.')}
                </p>
              </div>
            </div>

            {/* Default Business Banner */}
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="banner_upload">{t('admin.community.business.banner', 'Default Business Banner')}</Label>
              <div className="space-y-4">
                {formData.default_business_banner_url && (
                  <div className="relative rounded-md overflow-hidden border">
                    <AspectRatio ratio={16/5}>
                      <img 
                        src={formData.default_business_banner_url} 
                        alt="Default business banner" 
                        className="w-full h-full object-cover"
                      />
                    </AspectRatio>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {/* Hidden the actual URL input field as requested */}
                  <input 
                    type="hidden" 
                    id="default_business_banner_url"
                    name="default_business_banner_url"
                    value={formData.default_business_banner_url || ''}
                  />
                  <div className="relative w-full">
                    <Input 
                      type="file" 
                      id="banner_upload" 
                      className="hidden" 
                      accept="image/*"
                      onChange={handleBannerUpload}
                      disabled={uploading}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      className="w-full"
                      disabled={uploading}
                      onClick={() => document.getElementById('banner_upload')?.click()}
                    >
                      {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                      {t('admin.community.banner.upload', 'Upload Banner Image')}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('admin.community.business.banner.help', 'This image will be used as the default banner for businesses that don\'t upload their own. Remember to click Save Changes to apply this update.')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Localization Section - Unchanged but repositioned */}
        <Card className="admin-card admin-card-stats mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">{t('admin.community.localization.title', 'Localization')}</CardTitle>
            <CardDescription className="text-muted-foreground/80">
              {t('admin.community.localization.description', 'Configure language settings for your community')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="default_locale">{t('admin.community.locale.default', 'Default Locale')}</Label>
              <Select 
                value={formData.default_locale} 
                onValueChange={handleDefaultLocaleChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('admin.community.locale.select', 'Select default locale')} />
                </SelectTrigger>
                <SelectContent>
                  {formData.available_locales.map(locale => (
                    <SelectItem key={locale} value={locale}>
                      {locale}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('admin.community.locale.available', 'Available Locales')}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {formData.available_locales.map(locale => (
                  <div key={locale} className="flex items-center space-x-3 p-3 bg-gray-50/50 rounded-lg">
                    <Switch
                      id={`locale-${locale}`}
                      checked={selectedLocales.includes(locale)}
                      onCheckedChange={() => handleLocaleToggle(locale)}
                      disabled={locale === formData.default_locale}
                    />
                    <Label htmlFor={`locale-${locale}`} className="font-medium">
                      {locale}{locale === formData.default_locale && t('admin.community.locale.default.tag', ' (Default)')}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New Advanced Settings Section - Collapsible */}
        <Card className="admin-card admin-card-stats mb-6">
          <Collapsible open={isAdvancedOpen} onOpenChange={setIsAdvancedOpen}>
            <CardHeader className="pb-3">
              <CollapsibleTrigger className="flex items-center justify-between w-full focus:outline-none group hover:bg-gray-50/50 -mx-6 px-6 py-2 rounded-lg transition-colors">
                <div className="flex items-center">
                  <CardTitle className="text-lg font-semibold text-slate-800">{t('admin.community.advanced.title', 'Advanced Settings')}</CardTitle>
                </div>
                {isAdvancedOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground group-hover:text-slate-700" /> : <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-slate-700" />}
              </CollapsibleTrigger>
              <CardDescription>
                {t('admin.community.advanced.description', 'Configure advanced settings for your community')}
              </CardDescription>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="allowed_frontend_domain">{t('admin.community.domain', 'Domain')}</Label>
                  <Input 
                    id="allowed_frontend_domain" 
                    name="allowed_frontend_domain" 
                    value={formData.allowed_frontend_domain} 
                    onChange={handleInputChange} 
                    placeholder={t('admin.community.domain.placeholder', 'e.g., example.com')} 
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('admin.community.domain.description', 'The domain used to identify your community')}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="community_app_url">{t('admin.community.app_url', 'Community App URL')}</Label>
                  <Input 
                    id="community_app_url" 
                    name="community_app_url" 
                    value={formData.community_app_url} 
                    onChange={handleInputChange} 
                    placeholder={t('admin.community.app_url.placeholder', 'e.g., https://app.example.com')} 
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('admin.community.app_url.description', 'The URL used for links in notifications')}
                  </p>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <Button type="submit" disabled={updateMutation.isPending} className="w-full sm:w-auto gap-2 rounded-lg  px-6 py-2.5">
            {updateMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            {!updateMutation.isPending && <Save className="h-4 w-4" />}
            {t('common.save.changes', 'Save Changes')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CommunityManagement;
